export default function TaxConsiderations() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Tax Implications of Selling Inherited Apartments
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Understanding the tax benefits of inherited property sales can save you tens of thousands of dollars. Here's what you need to know.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl p-8 text-white mb-8">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg mb-6">
                <i className="ri-line-chart-line text-4xl"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4">The Step-Up in Basis Advantage</h3>
              <p className="text-xl text-white/90 mb-6 leading-relaxed">
                When you inherit property, the IRS "steps up" the cost basis to the fair market value at the date of death. This is one of the most powerful tax benefits in real estate.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h4 className="text-xl font-bold mb-4">Example Scenario:</h4>
                <div className="space-y-3 text-lg">
                  <div className="flex justify-between pb-2 border-b border-white/20">
                    <span className="text-white/80">Original purchase (1990):</span>
                    <span className="font-bold">$200,000</span>
                  </div>
                  <div className="flex justify-between pb-2 border-b border-white/20">
                    <span className="text-white/80">Value at inheritance (2024):</span>
                    <span className="font-bold">$1,200,000</span>
                  </div>
                  <div className="flex justify-between pb-2 border-b border-white/20">
                    <span className="text-white/80">Your new stepped-up basis:</span>
                    <span className="font-bold">$1,200,000</span>
                  </div>
                  <div className="flex justify-between pt-3">
                    <span className="text-white/90">Capital gains if sold at $1,200,000:</span>
                    <span className="font-bold text-2xl text-teal-200">$0</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h4 className="text-2xl font-bold mb-4 text-gray-900">Without Step-Up Basis</h4>
              <p className="text-gray-700 mb-4 leading-relaxed">
                If you had purchased the property yourself for $200,000 and sold it for $1,200,000:
              </p>
              <div className="space-y-2 text-lg">
                <div className="flex justify-between">
                  <span className="text-gray-700">Capital gain:</span>
                  <span className="font-bold text-gray-900">$1,000,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Federal tax (20%):</span>
                  <span className="font-bold text-red-600">$200,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">State tax (varies):</span>
                  <span className="font-bold text-red-600">$50,000+</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-gray-300">
                  <span className="font-bold text-gray-900">Total tax burden:</span>
                  <span className="font-bold text-2xl text-red-600">$250,000+</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/ae81f5918d30aca65b0e29b35c40827d.jpg"
              alt="inherited apartment building tax implications"
              className="w-full h-full rounded-2xl shadow-2xl object-cover"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-6">
              <i className="ri-calendar-line text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Timing Considerations</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The step-up basis is locked in at the date of death. However, property values can change:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-teal-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700"><strong className="text-gray-900">Sell quickly:</strong> Minimize capital gains if property appreciates after inheritance</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-teal-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700"><strong className="text-gray-900">Market decline:</strong> If value drops, you may have a capital loss to deduct</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-teal-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700"><strong className="text-gray-900">Depreciation recapture:</strong> Not applicable if you never rented the property yourself</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-6">
              <i className="ri-file-text-line text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Estate Tax Considerations</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Federal estate tax only applies to estates exceeding $13.61 million (2024). However:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-teal-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700"><strong className="text-gray-900">State estate taxes:</strong> Some states have lower thresholds ($1-5 million)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-teal-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700"><strong className="text-gray-900">Property valuation:</strong> Professional appraisal establishes basis and estate value</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-circle-fill text-teal-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700"><strong className="text-gray-900">Quick sale benefits:</strong> Reduces estate administration costs and complexity</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-xl p-8">
          <div className="flex items-start gap-4">
            <i className="ri-error-warning-line text-yellow-600 text-3xl flex-shrink-0"></i>
            <div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Important Tax Disclaimer</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tax laws are complex and vary by state. The information provided here is general guidance only and should not be considered tax advice. We strongly recommend consulting with a qualified tax professional or CPA who specializes in inherited property before making any decisions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-gray-900">We can connect you with experienced tax advisors</strong> who understand inherited real estate transactions and can help you maximize your tax benefits while ensuring compliance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
